import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ArrowRight, MapPin, Users, Calendar, ChevronLeft, Copy, Check, Loader2 } from "lucide-react";
import WaitlistForm from "./WaitlistForm";
import { BLOG_POSTS, SHOP_COLLECTIONS, SHOPIFY_STORE_URL, CHAPTER_LOCATIONS, UPCOMING_EVENTS } from "../../constants";
import axios from "axios";
import DOMPurify from "dompurify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Section = ({ children, className = "", id }) => (
  <section id={id} className={`py-24 px-6 md:py-32 ${className}`}>
    <div className="max-w-7xl mx-auto">
      {children}
    </div>
  </section>
);

export const Button = ({ children, variant = "primary", href = "#", className = "", onClick, disabled, type = "button" }) => {
  const baseStyles = "inline-flex items-center justify-center px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300";
  const variants = {
    primary: "bg-lws-green text-lws-paper hover:bg-lws-navy",
    secondary: "bg-lws-amber text-lws-paper hover:bg-lws-rose",
    outline: "border border-lws-ink/20 text-lws-ink hover:border-lws-green hover:text-lws-green",
  };

  if (onClick || type === "submit") {
    return (
      <button 
        type={type}
        onClick={onClick} 
        disabled={disabled}
        className={`${baseStyles} ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {children}
      </button>
    );
  }

  return (
    <a href={href} className={`${baseStyles} ${variants[variant]} ${className}`}>
      {children}
    </a>
  );
};

// --- HOME PAGE ---
export const HomePage = () => {
  const [copied, setCopied] = useState(false);
  const promptText = "Write to someone who changed your mind about something important.";

  const handleCopy = () => {
    navigator.clipboard.writeText(promptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {/* HERO SECTION */}
      <Section className="min-h-[80vh] flex items-center" id="home">
        <div className="max-w-4xl" data-testid="hero-section">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xs uppercase tracking-[0.4em] text-lws-taupe mb-6 block font-semibold"
          >
            Letter Writers Society
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-6xl md:text-8xl font-serif tracking-tighter leading-[0.9] mb-10 text-lws-green"
            data-testid="hero-title"
          >
            Reclaiming the lost art of <span className="italic text-lws-amber">letter writing.</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-xl md:text-2xl font-serif text-lws-taupe max-w-2xl leading-relaxed mb-12"
          >
            A human-first, ritual-based correspondence society built in response to disposable digital communication. We conjure letterbox joy.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-6"
          >
            <Button href="#membership" data-testid="hero-cta-primary">Enter the Society</Button>
            <Button variant="outline" href="#about" data-testid="hero-cta-secondary">Our Philosophy</Button>
          </motion.div>
        </div>
      </Section>

      {/* THE PREMISE */}
      <Section className="bg-lws-green text-lws-paper overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
          <div className="space-y-10">
            <h2 className="text-5xl md:text-7xl leading-tight font-serif">
              Connection, <br />
              <span className="italic text-lws-amber">not perfection.</span>
            </h2>
            <div className="space-y-6 text-lg font-light leading-relaxed text-lws-paper/80">
              <p>
                Letter Writers Society rejects the idea that handwriting must be beautiful to be meaningful. 
                We welcome the crossed-out words, the ink smudges, and the messy lines.
              </p>
              <p>
                Letters were never meant to be perfect. They were meant to be real. 
              </p>
            </div>
            <div className="pt-6">
              <Button variant="secondary" href="#membership">Join the Movement</Button>
            </div>
          </div>
          <div className="relative">
            <motion.div 
              initial={{ rotate: -5, scale: 0.9, opacity: 0 }}
              whileInView={{ rotate: 2, scale: 1, opacity: 1 }}
              transition={{ duration: 1 }}
              className="bg-lws-paper p-12 shadow-2xl transform rotate-2"
            >
              <div className="font-serif text-lws-ink space-y-4 text-xl italic leading-relaxed opacity-60">
                <p>Dear Friend,</p>
                <p>I was thinking about the way we used to talk—before the screens, before the speed. I miss the weight of your words on paper.</p>
                <p className="line-through">I hope you are well.</p>
                <p>I know you are well.</p>
                <p>Write back soon.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* WEEKLY PROMPT */}
      <Section className="bg-lws-paper border-y border-lws-ink/5">
        <div className="max-w-3xl mx-auto text-center space-y-12">
          <span className="text-xs uppercase tracking-[0.4em] text-lws-amber font-semibold">The Daily Ritual</span>
          <div className="relative group">
            <h2 className="text-4xl md:text-5xl font-serif italic">"{promptText}"</h2>
            <button 
              onClick={handleCopy}
              className="mt-8 inline-flex items-center text-[10px] uppercase tracking-[0.3em] text-lws-taupe hover:text-lws-green transition-colors"
              data-testid="copy-prompt-btn"
            >
              {copied ? (
                <>
                  <Check size={14} className="mr-2" /> Copied to clipboard
                </>
              ) : (
                <>
                  <Copy size={14} className="mr-2" /> Copy Prompt
                </>
              )}
            </button>
          </div>
          <p className="text-lws-taupe text-lg font-serif leading-relaxed">
            365 prompts designed to help you bypass the small talk and reach for something more meaningful. 
            Each paired with a historical letter from the archive.
          </p>
          <div className="pt-8">
            <Button variant="outline" href="#prompts">Explore the Prompt Deck</Button>
          </div>
        </div>
      </Section>

      {/* ECOSYSTEM GRID */}
      <Section id="society">
        <div className="text-center mb-24">
          <h2 className="text-5xl md:text-6xl mb-6 font-serif">Join the Society</h2>
          <p className="text-lws-taupe max-w-xl mx-auto text-lg font-serif">
            A modern counter-digital movement restoring presence, ritual, and human connection.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { title: "Events & Salons", desc: "One-off themed gatherings, pop-ups, and seasonal activations.", icon: Calendar, href: "#events" },
            { title: "Local Chapters", desc: "Small, thoughtful gatherings for quiet writing time, tea, and ceremonial cacao.", icon: MapPin, href: "#chapters" },
            { title: "Membership", desc: "A society for the intentional. Access prompts, rituals, and a global community.", icon: Users, href: "#membership" },
          ].map((item, idx) => (
            <motion.div 
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.2 }}
              className="group p-10 border border-lws-ink/5 hover:border-lws-amber/30 transition-all duration-500 bg-white/30 backdrop-blur-sm"
            >
              <item.icon className="text-lws-amber mb-8" size={32} strokeWidth={1.5} />
              <h3 className="text-3xl mb-4 font-serif">{item.title}</h3>
              <p className="text-lws-taupe font-light leading-relaxed mb-8">{item.desc}</p>
              <a href={item.href} className="text-xs uppercase tracking-widest font-semibold text-lws-green flex items-center hover:translate-x-2 transition-transform">
                Learn More <ArrowRight className="ml-2" size={14} />
              </a>
            </motion.div>
          ))}
        </div>
      </Section>
    
      {/* READ PREVIEW */}
      <Section className="bg-lws-paper">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
          <div className="aspect-[4/5] bg-lws-taupe/10 overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1526721940322-10fb6e3ae94a?w=800&h=1000&fit=crop" 
              alt="Epistolary Literature" 
              className="w-full h-full object-cover opacity-80"
              loading="lazy"
            />
          </div>
          <div className="space-y-10">
            <span className="text-xs uppercase tracking-[0.4em] text-lws-amber font-semibold">The Library</span>
            <h2 className="text-5xl md:text-7xl leading-tight font-serif">Books told through <span className="italic">letters.</span></h2>
            <p className="text-xl text-lws-taupe font-serif leading-relaxed">
              Explore our curated guide to epistolary literature—from gothic classics to modern digital masterpieces. 
              Discover why the unmediated intimacy of the written word remains one of literature's most powerful forms.
            </p>
            <Button variant="outline" href="#read">Explore the Genre</Button>
          </div>
        </div>
      </Section>

      <WaitlistForm 
        title="Become a founding member." 
        subtitle="Join the waitlist to be notified when membership tiers open and local chapters begin to form in your city."
        tag="membership"
      />
    </>
  );
};

// --- EVENTS PAGE ---
export const EventsPage = () => (
  <>
    <Section id="events">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-24">
        <div className="max-w-4xl">
          <span className="text-xs uppercase tracking-[0.4em] text-lws-amber mb-4 block font-semibold">The Salon</span>
          <h2 className="text-6xl md:text-8xl mb-8 leading-tight font-serif">One-off <span className="italic">experiences.</span></h2>
          <p className="text-xl text-lws-taupe font-serif leading-relaxed">
            Events are discovery and activation tools that can lead into future chapter creation. 
            From seasonal pop-ups to themed correspondence evenings.
          </p>
        </div>
        <div className="aspect-[4/5] bg-lws-taupe/10 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=800&h=1000&fit=crop" 
            alt="One-off experiences" 
            className="w-full h-full object-cover opacity-80"
            loading="lazy"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-testid="events-list">
        {UPCOMING_EVENTS.map((event, idx) => (
          <div key={idx} className="group border border-lws-ink/5 p-12 hover:bg-lws-green hover:text-lws-paper transition-all duration-500" data-testid={`event-${idx}`}>
            <span className="text-xs uppercase tracking-widest text-lws-amber mb-4 block font-bold">{event.date}</span>
            <h3 className="text-4xl mb-4 font-serif">{event.title}</h3>
            <p className="text-lws-taupe group-hover:text-lws-paper/70 mb-8 flex items-center">
              <MapPin size={16} className="mr-2" /> {event.loc}
            </p>
            <Button variant={event.status === 'Live' ? 'primary' : 'outline'} href="#waitlist">
              {event.status === 'Live' ? 'Book Seat' : 'Join Waitlist'}
            </Button>
          </div>
        ))}
      </div>
    </Section>
  </>
);

// --- CHAPTERS PAGE ---
export const ChaptersPage = () => (
  <>
    <Section id="chapters">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
        <div className="space-y-10">
          <span className="text-xs uppercase tracking-[0.4em] text-lws-green font-semibold">Local Chapters</span>
          <h2 className="text-5xl md:text-7xl leading-tight font-serif">Small, thoughtful <span className="italic">gatherings.</span></h2>
          <p className="text-xl text-lws-taupe font-serif leading-relaxed">
            Chapters are recurring location-based gatherings where members come together for quiet writing time, 
            shared materials, and optional reflection.
          </p>
          <Button variant="outline" href="#hosting">Express Interest in Hosting</Button>
        </div>
        <div className="bg-lws-taupe/5 p-12 min-h-[400px] flex flex-col">
          <div className="flex items-center justify-between mb-8 border-b border-lws-ink/10 pb-4">
            <h3 className="text-xl font-serif">Active & Forming Chapters</h3>
            <MapPin size={20} className="text-lws-amber" />
          </div>
          <div className="space-y-6 overflow-y-auto max-h-[400px] pr-4 custom-scrollbar" data-testid="chapters-list">
            {CHAPTER_LOCATIONS.map((loc, idx) => (
              <div key={idx} className="flex items-center justify-between group" data-testid={`chapter-${idx}`}>
                <div>
                  <h4 className="text-lg font-serif">{loc.city}</h4>
                  <p className="text-xs text-lws-taupe uppercase tracking-widest">{loc.state}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-[9px] uppercase tracking-widest px-2 py-1 font-bold ${loc.status === 'Active' ? 'bg-lws-green/10 text-lws-green' : 'bg-lws-amber/10 text-lws-amber'}`}>
                    {loc.status}
                  </span>
                  <a href="#waitlist" className="text-[10px] uppercase tracking-widest font-bold text-lws-ink hover:text-lws-green transition-colors">
                    {loc.status === 'Active' ? 'Join' : 'Enquire'}
                  </a>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-lws-taupe/60 italic mt-auto pt-8">
            Don't see your city? Join the waitlist below to help us decide where to open next.
          </p>
        </div>
      </div>
    </Section>
    <WaitlistForm 
      title="Bring Letter Writers Society to your city." 
      subtitle="Express your interest in joining or hosting a local chapter. We form chapters based on demand."
      tag="chapters"
    />
  </>
);

// --- JOURNAL PAGE ---
export const JournalPage = () => (
  <Section id="journal">
    <div className="max-w-4xl mb-24">
      <span className="text-xs uppercase tracking-[0.4em] text-lws-amber mb-4 block font-semibold">The Journal</span>
      <h2 className="text-6xl md:text-8xl mb-8 leading-tight font-serif">Thoughtful <span className="italic">publications.</span></h2>
      <p className="text-xl text-lws-taupe font-serif leading-relaxed">
        Editorial writing, practical letter-writing guidance, prompts, and community stories.
      </p>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="journal-posts">
      {BLOG_POSTS.map((post, idx) => {
        const isVideo = post.img.endsWith('.mp4') || post.img === "perfection-enemy-meaningful-letter";
        const isLocal = post.img.includes('.') || post.img === "perfection-enemy-meaningful-letter";
        const localSrc = post.img === "perfection-enemy-meaningful-letter" ? "/perfection-enemy-meaningful-letter.mp4" : `/${post.img}`;
        const imgSrc = isLocal ? localSrc : `https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600&h=800&fit=crop&seed=${post.img}`;
        
        return (
          <div key={idx} className="group cursor-pointer flex flex-col h-full" data-testid={`journal-post-${idx}`}>
            <div className="aspect-[4/5] bg-lws-taupe/5 mb-6 overflow-hidden">
              {isVideo ? (
                <video 
                  src={localSrc} 
                  autoPlay 
                  loop 
                  muted 
                  playsInline 
                  className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                />
              ) : (
                <img 
                  src={imgSrc} 
                  alt={post.title} 
                  className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              )}
            </div>
            <div className="flex flex-col flex-grow">
              <span className="text-[10px] uppercase tracking-[0.3em] text-lws-amber mb-2 font-bold">{post.category}</span>
              <h4 className="text-xl mb-4 group-hover:text-lws-green transition-colors leading-tight font-serif">{post.title}</h4>
              <p className="text-sm text-lws-taupe font-serif leading-relaxed mb-6 line-clamp-3 flex-grow">
                {post.excerpt}
              </p>
              <Button variant="outline" href={`#blog-post/${post.id}`} className="w-full">Read now</Button>
            </div>
          </div>
        );
      })}
    </div>
  </Section>
);

// --- BLOG POST PAGE ---
export const BlogPostPage = ({ id }) => {
  const post = BLOG_POSTS.find(p => p.id === id);
  if (!post) return <Section><p className="text-center text-xl">Post not found.</p></Section>;

  const isVideo = post.img.endsWith('.mp4') || post.img === "perfection-enemy-meaningful-letter";
  const isLocal = post.img.includes('.');
  const mediaSrc = post.img === "perfection-enemy-meaningful-letter" 
    ? "/perfection-enemy-meaningful-letter.mp4" 
    : isLocal 
      ? `/${post.img}` 
      : `https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1200&h=675&fit=crop&seed=${post.img}`;

  return (
    <Section id="blog-post">
      <div className="max-w-3xl mx-auto" data-testid="blog-post">
        <a href="#journal" className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-12 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> Back to Journal
        </a>
        
        <div className="mb-12">
          <span className="text-xs uppercase tracking-[0.4em] text-lws-amber mb-4 block font-semibold">
            {post.category} — {post.date}
          </span>
          <h1 className="text-5xl md:text-7xl font-serif tracking-tighter leading-[1.1] mb-8 text-lws-green" data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.standfirst && (
            <p className="text-2xl font-serif text-lws-taupe leading-relaxed italic opacity-80">
              {post.standfirst}
            </p>
          )}
        </div>

        <div className="aspect-[16/9] bg-lws-taupe/10 mb-16 overflow-hidden">
          {isVideo ? (
            <video 
              src={mediaSrc} 
              autoPlay 
              loop 
              muted 
              playsInline 
              className="w-full h-full object-cover opacity-80"
            />
          ) : (
            <img 
              src={mediaSrc} 
              alt={post.title} 
              className="w-full h-full object-cover opacity-80"
              loading="lazy"
            />
          )}
        </div>

        <div className="relative">
          {post.pullQuote && (
            <div className="mb-16 border-l-4 border-lws-amber pl-8 py-4">
              <p className="text-3xl font-serif text-lws-green leading-tight">
                "{post.pullQuote}"
              </p>
            </div>
          )}
          
          <div 
            className="prose prose-xl font-serif text-lws-taupe leading-relaxed space-y-8 max-w-none [&>p]:mb-6"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />
        </div>

        <div className="mt-24 pt-12 border-t border-lws-ink/5">
          <h3 className="text-2xl mb-8 font-serif">Continue the conversation.</h3>
          <Button href="#membership">Join the Society</Button>
        </div>
      </div>
    </Section>
  );
};

// --- ABOUT PAGE ---
export const AboutPage = () => (
  <>
    <Section id="about">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
        <div className="space-y-12">
          <span className="text-xs uppercase tracking-[0.4em] text-lws-green font-semibold">Our Philosophy</span>
          <h2 className="text-5xl md:text-7xl leading-tight font-serif">
            A modern <span className="italic">counter-digital</span> movement.
          </h2>
          <div className="space-y-8 text-xl font-serif text-lws-taupe leading-relaxed">
            <p>
              Letter Writers Society exists to restore presence, thoughtfulness, and slowness to our communication. 
              In a world of disposable digital noise, a handwritten letter is a tangible memory.
            </p>
            <p>
              It is not about the paper. It is about what happens when you sit down to write. 
              The ritual of reflection. The anticipation of the envelope. The intimacy of the exchange.
            </p>
          </div>
        </div>
        <div className="aspect-[4/5] bg-lws-taupe/10 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1517842645767-c639042777db?w=800&h=1000&fit=crop" 
            alt="Our Philosophy" 
            className="w-full h-full object-cover opacity-80"
            loading="lazy"
          />
        </div>
      </div>
    </Section>

    <Section className="bg-lws-taupe/5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
        <div className="aspect-[4/5] bg-lws-taupe/10 overflow-hidden order-2 md:order-1 relative">
          <img 
            src="/founder-portrait.jpg" 
            alt="Frederica Dillon" 
            className="w-full h-full object-cover opacity-90 transition-all duration-1000"
            style={{ objectPosition: '50% 20%' }}
          />
        </div>
        <div className="space-y-12 order-1 md:order-2">
          <span className="text-xs uppercase tracking-[0.4em] text-lws-amber font-semibold">Founder</span>
          <h2 className="text-5xl md:text-7xl leading-tight font-serif">
            Meet <span className="italic">Frederica Dillon.</span>
          </h2>
          <div className="space-y-8 text-xl font-serif text-lws-taupe leading-relaxed">
            <p>
              Letter Writers Society was born from Frederica's desire to reconnect with the human element of communication. 
              She believed that the speed of digital life was costing us our depth.
            </p>
            <p>
              "We don't need more efficiency. We need more meaning. Letter writing is the ultimate act of intentionality."
            </p>
          </div>
        </div>
      </div>
    </Section>
  </>
);

// --- HOSTING PAGE ---
export const HostingPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    location: "",
    reason: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await axios.post(`${BACKEND_URL}/api/hosting-application`, formData);
      setIsSubmitted(true);
    } catch (err) {
      console.error("Hosting application error:", err);
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Section id="hosting">
      <div className="max-w-3xl mx-auto">
        <span className="text-xs uppercase tracking-[0.4em] text-lws-green mb-4 block font-semibold">Host a Chapter</span>
        <h1 className="text-5xl md:text-7xl font-serif tracking-tighter leading-tight mb-8 text-lws-green">Bring the Letter Writers Society to your area.</h1>
        
        <div className="space-y-8 text-xl font-serif text-lws-taupe leading-relaxed mb-16">
          <p>
            Letter Writers Society Chapters are intimate, thoughtfully held gatherings centred around handwritten correspondence, reflection, and meaningful connection. We're building a growing network of hosts who can create calm, welcoming spaces for people to slow down and write.
          </p>
          <p>
            If you feel aligned with the spirit of the Society, you're invited to express interest in hosting a chapter.
          </p>
          <p>
            You do not need to have everything figured out before applying. We're looking for people who understand the tone of Letter Writers Society and can hold a space with care, warmth, and intention.
          </p>
        </div>

        <div className="bg-lws-taupe/5 p-12 md:p-16">
          <AnimatePresence mode="wait">
            {!isSubmitted ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <h2 className="text-3xl font-serif mb-6">Express Interest</h2>
                <p className="text-lws-taupe mb-10">Complete the form below to tell us a little about yourself, your location, and why you'd like to host a chapter.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="hosting-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input 
                      type="text" 
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required 
                      placeholder="Full Name" 
                      className="w-full bg-white border border-lws-ink/10 px-6 py-4 focus:outline-none focus:border-lws-green" 
                      data-testid="hosting-name"
                    />
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required 
                      placeholder="Email Address" 
                      className="w-full bg-white border border-lws-ink/10 px-6 py-4 focus:outline-none focus:border-lws-green" 
                      data-testid="hosting-email"
                    />
                  </div>
                  <input 
                    type="text" 
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required 
                    placeholder="Location (City, Country)" 
                    className="w-full bg-white border border-lws-ink/10 px-6 py-4 focus:outline-none focus:border-lws-green" 
                    data-testid="hosting-location"
                  />
                  <textarea 
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    required 
                    placeholder="Why would you like to host a chapter?" 
                    rows={6} 
                    className="w-full bg-white border border-lws-ink/10 px-6 py-4 focus:outline-none focus:border-lws-green"
                    data-testid="hosting-reason"
                  ></textarea>
                  
                  {error && (
                    <p className="text-red-600 text-sm" data-testid="hosting-error">{error}</p>
                  )}

                  <button 
                    type="submit" 
                    disabled={isLoading}
                    className="w-full bg-lws-green text-lws-paper px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-lws-navy transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="hosting-submit"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Apply to Host"
                    )}
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12 space-y-6"
                data-testid="hosting-success"
              >
                <div className="w-20 h-20 bg-lws-green rounded-full flex items-center justify-center mx-auto mb-8">
                  <Check className="text-lws-paper" size={40} />
                </div>
                <h2 className="text-4xl font-serif leading-tight">Application Received.</h2>
                <p className="text-lg text-lws-taupe font-serif leading-relaxed">
                  Thank you for your interest in hosting. We review every application with care and will be in touch soon.
                </p>
                <button 
                  onClick={() => {
                    setIsSubmitted(false);
                    setFormData({ fullName: "", email: "", location: "", reason: "" });
                  }}
                  className="text-[10px] uppercase tracking-[0.3em] text-lws-amber hover:text-lws-green transition-colors pt-8"
                >
                  Submit another application
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          
          {!isSubmitted && (
            <p className="text-xs text-lws-taupe/60 mt-8 text-center italic">
              All requests are reviewed carefully, and if it feels like a fit, you'll hear from us shortly.
            </p>
          )}
        </div>
      </div>
    </Section>
  );
};

// --- SHOP PAGE ---
export const ShopPage = () => {
  const storeUrl = SHOPIFY_STORE_URL;

  return (
    <Section id="shop">
      <div className="text-center mb-24">
        <h2 className="text-5xl md:text-6xl mb-6 font-serif">The Collection</h2>
        <p className="text-lws-taupe max-w-xl mx-auto text-lg font-serif">
          Tools for the ritual. Curated objects designed to support the movement.
        </p>
      </div>

      <div className="space-y-32" data-testid="shop-collections">
        {SHOP_COLLECTIONS.map((collection, cIdx) => (
          <div key={cIdx}>
            <h3 className="text-3xl font-serif mb-12 border-b border-lws-ink/5 pb-4">{collection.title}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
              {collection.items.map((item, idx) => {
                const isLive = item.status === 'Live';
                const productUrl = isLive ? `${storeUrl}/products/${item.handle}` : "#waitlist";
                
                return (
                  <a 
                    key={idx} 
                    href={productUrl}
                    target={isLive ? "_blank" : "_self"}
                    rel={isLive ? "noopener noreferrer" : ""}
                    className="group cursor-pointer block"
                    data-testid={`shop-item-${idx}`}
                  >
                    <div className="aspect-[3/4] bg-lws-taupe/5 mb-6 overflow-hidden relative">
                      <img 
                        src={`https://images.unsplash.com/photo-1544816155-12df9643f363?w=600&h=800&fit=crop&seed=${item.img}`} 
                        alt={item.name} 
                        className="w-full h-full object-cover opacity-90 group-hover:scale-110 transition-transform duration-1000"
                        loading="lazy"
                      />
                      <div className="absolute top-4 right-4">
                        <span className={`text-[9px] uppercase tracking-widest px-3 py-1.5 font-bold ${isLive ? 'bg-lws-green text-lws-paper' : 'bg-lws-taupe text-lws-paper'}`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                    <h4 className="text-xl mb-1 group-hover:text-lws-green transition-colors font-serif">{item.name}</h4>
                    <p className="text-sm text-lws-taupe font-light">{item.price}</p>
                    {!isLive && (
                      <p className="text-[10px] uppercase tracking-widest text-lws-amber mt-2 font-bold group-hover:translate-x-1 transition-transform inline-flex items-center">
                        Notify Me <ArrowRight size={10} className="ml-1" />
                      </p>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-32">
        <WaitlistForm 
          title="The Subscription Box" 
          subtitle="A seasonal delivery of curated stationery, prompts, and rituals. Join the waitlist for our inaugural box."
          tag="subbox"
        />
      </div>
    </Section>
  );
};

// --- CHALLENGE PAGE ---
export const ChallengePage = () => (
  <>
    <Section id="challenge">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
        <div className="space-y-10">
          <span className="text-xs uppercase tracking-[0.4em] text-lws-amber font-semibold">Upcoming Challenge</span>
          <h2 className="text-5xl md:text-7xl leading-tight font-serif">The 30-Day <span className="italic">Correspondence</span> Challenge.</h2>
          <p className="text-xl text-lws-taupe font-serif leading-relaxed">
            A month-long journey to reclaim your presence through the written word. 
            Join a global community of letter writers as we slow down, reflect, and connect.
          </p>
          <div className="space-y-6">
            {[
              { title: "Downloadable Stationery", desc: "Exclusive printable designs for your letters." },
              { title: "Daily Prompts & Rituals", desc: "Guided reflections to help you find your voice." },
              { title: "Skool Community Access", desc: "Connect with fellow writers in our private space." },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-lws-green/10 flex items-center justify-center mt-1">
                  <Check size={14} className="text-lws-green" />
                </div>
                <div>
                  <h4 className="font-bold text-lws-ink">{item.title}</h4>
                  <p className="text-sm text-lws-taupe">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="pt-6">
            <Button href="#challenge">Sign Up for the Challenge</Button>
          </div>
          <p className="text-xs text-lws-taupe italic">Beginning Date: TBC — Join the waitlist to be the first to know.</p>
        </div>
        <div className="aspect-[4/5] bg-lws-taupe/10 overflow-hidden relative">
          <img 
            src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=1000&fit=crop" 
            alt="30-Day Correspondence Challenge" 
            className="w-full h-full object-cover opacity-80"
            loading="lazy"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-lws-paper/90 backdrop-blur-sm p-12 text-center max-w-xs shadow-2xl transform -rotate-2">
              <span className="text-[10px] uppercase tracking-[0.3em] text-lws-amber font-bold block mb-4">Coming Soon</span>
              <h3 className="text-3xl font-serif mb-4">The Ritual of Beginning</h3>
              <p className="text-sm text-lws-taupe font-serif italic">"A letter is a gift that travels across time."</p>
            </div>
          </div>
        </div>
      </div>
    </Section>

    <WaitlistForm 
      title="Join the Challenge Waitlist" 
      subtitle="Sign up to receive your starter kit and access to the Skool community when we launch."
      tag="challenge"
    />
  </>
);

// Export ReadPage separately due to its size - will be in ReadPage.jsx
export { default as ReadPage } from "./ReadPage";
